import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { raClient } from '@/lib/raClient'
import { useAuthStore } from '@/stores/authStore'
import { useIdentity } from './useIdentity'

export interface User {
  id: number
  first_name: string
  last_name: string
  email: string
  customer_id?: number
  partner_id?: number
  /** Computed full name for display */
  name?: string
}

export interface Team {
  id: number
  name: string
}

export interface SupportUserInfo {
  id: number
  first_name: string | null
  last_name: string | null
  email: string
}

// Default Bonnie Support user when no partner support is configured
const BONNIE_SUPPORT_USER: SupportUserInfo = {
  id: 133,
  first_name: 'Team Bonnie',
  last_name: '(Bonnie Support)',
  email: 'support@bonnie.co',
}

// Query key factory
export const userKeys = {
  all: ['users'] as const,
  list: (filter?: Record<string, unknown>) => [...userKeys.all, 'list', filter] as const,
  byIds: (ids: number[]) => [...userKeys.all, 'byIds', ids] as const,
  supportUser: (partnerId?: string, customerId?: string) =>
    [...userKeys.all, 'supportUser', partnerId, customerId] as const,
}

export const teamKeys = {
  all: ['teams'] as const,
  list: () => [...teamKeys.all, 'list'] as const,
}

/**
 * Hook to fetch users list
 * @param customerId - Filter users by customer ID
 * @param enabled - Whether to enable the query
 * @param includePartnerUsers - When true, also includes users from the logged-in user's partner (default: true)
 */
export function useUsers(customerId?: number, enabled = true, includePartnerUsers = true) {
  // Use identity from /me endpoint which has partner_id
  const { user: identity, isLoading: identityLoading } = useIdentity()
  
  // Get partner_id from identity
  const partnerId = identity?.partner_id
  
  // Check if identity is ready (loaded and has essential data)
  const identityReady = !identityLoading && identity !== undefined

  return useQuery({
    // Include identity-dependent values in query key so it re-runs when identity loads
    queryKey: userKeys.list({ customerId, partnerId, includePartnerUsers }),
    queryFn: async (): Promise<User[]> => {
      const params = new URLSearchParams()
      params.set('sort', JSON.stringify(['first_name', 'ASC']))
      params.set('range_', JSON.stringify([0, 999]))

      // Build filter based on customerId and includePartnerUsers:
      // - When includePartnerUsers is true (default): include customer users AND partner users
      //   (used for assignment targets in conversations)
      // - When includePartnerUsers is false: only include customer users
      //   (used for notification recipient, matching v1 behavior)
      let filter: Record<string, unknown> = {}
      if (customerId) {
        if (includePartnerUsers && partnerId) {
          filter = {
            _or: [
              { customer_id: customerId },
              { partner_id: partnerId },
            ],
          }
        } else {
          filter = { customer_id: customerId }
        }
      }
      params.set('filter', JSON.stringify(filter))

      const response = await api.post(`/users/ra/getList?${params.toString()}`)
      const data = response.data.data || response.data
      return data as User[]
    },
    // Wait for identity to be fully loaded before fetching users
    enabled: enabled && identityReady,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Hook to get specific users by IDs
 */
export function useUsersByIds(ids: number[], enabled = true) {
  return useQuery({
    queryKey: userKeys.byIds(ids),
    queryFn: async () => {
      const users = await raClient.getMany<User>('users', ids)
      // Add computed name field for display
      return users.map((u) => ({
        ...u,
        name: `${u.first_name} ${u.last_name}`,
      }))
    },
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch teams list
 */
export function useTeams(enabled = true) {
  return useQuery({
    queryKey: teamKeys.list(),
    queryFn: async (): Promise<Team[]> => {
      const params = new URLSearchParams()
      params.set('sort', JSON.stringify(['name', 'ASC']))
      params.set('range_', JSON.stringify([0, 99]))
      params.set('filter', JSON.stringify({}))

      const response = await api.post(`/teams/ra/getList?${params.toString()}`)
      const data = response.data.data || response.data
      return data as Team[]
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Hook to fetch the support user for a partner or customer
 * Returns the partner's designated support user, or falls back to Bonnie Support
 * @param overrideCustomerId - Optional customerId to use instead of logged-in user's customerId (e.g., from assistant)
 */
export function useSupportUser(overrideCustomerId?: number, enabled = true) {
  // Use identity data from /me endpoint which has partner_id
  const { user: identity, isLoading: identityLoading } = useIdentity()
  const authUser = useAuthStore((state) => state.user)
  
  // Partner ID from identity (from /me endpoint) - this matches v1 behavior
  const partnerId = identity?.partner_id
  // Use override customerId if provided, otherwise fall back to identity's customer_id
  const customerId = overrideCustomerId ?? identity?.customer_id

  return useQuery({
    queryKey: userKeys.supportUser(partnerId?.toString(), customerId?.toString()),
    queryFn: async (): Promise<SupportUserInfo> => {
      // Determine which endpoint to use
      // If logged-in user has a partner_id, use partner endpoint (matches v1 logic)
      // Otherwise, use customer endpoint with the (override or identity) customerId
      const endpoint = partnerId
        ? `/partners/support-user/${partnerId}`
        : customerId
          ? `/customers/support-user/${customerId}`
          : null

      if (!endpoint) {
        return BONNIE_SUPPORT_USER
      }

      try {
        const response = await api.get<SupportUserInfo>(endpoint)
        const supportUser = response.data

        // If support user is the same as logged-in user, use Bonnie support
        if (supportUser && supportUser.id === Number(authUser?.id || identity?.id)) {
          return BONNIE_SUPPORT_USER
        }

        // If we got a valid support user, format it as partner support
        if (supportUser && supportUser.id) {
          return {
            ...supportUser,
            last_name: `${supportUser.last_name || ''} (Partner Support)`,
          }
        }

        return BONNIE_SUPPORT_USER
      } catch {
        // On error, fall back to Bonnie Support
        return BONNIE_SUPPORT_USER
      }
    },
    // Wait for identity to load before fetching support user
    enabled: enabled && !identityLoading && !!(partnerId || customerId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Combined hook to get users and teams for assignment dropdowns
 * Puts the support user first in the list (matching v1 behavior)
 */
export function useAssignmentTargets(customerId?: number, enabled = true) {
  // Use identity from /me endpoint to match v1 behavior
  const { user: identity } = useIdentity()
  const { data: users = [], isLoading: usersLoading } = useUsers(customerId, enabled)
  const { data: teams = [], isLoading: teamsLoading } = useTeams(enabled)
  const { data: supportUser, isLoading: supportUserLoading } = useSupportUser(customerId, enabled)

  const userTargets = useMemo(() => {
    const targets: { id: number; name: string; type: 'user' }[] = []
    const existingIds = new Set<number>()

    // 1. For PARTNER role users, always add Bonnie support first
    if (identity?.role === 'partner') {
      targets.push({
        id: BONNIE_SUPPORT_USER.id,
        name: `${BONNIE_SUPPORT_USER.first_name} ${BONNIE_SUPPORT_USER.last_name}`,
        type: 'user' as const,
      })
      existingIds.add(BONNIE_SUPPORT_USER.id)
    }

    // 2. Add the support user (partner support or bonnie support)
    if (supportUser && !existingIds.has(supportUser.id)) {
      targets.push({
        id: supportUser.id,
        name: `${supportUser.first_name || ''} ${supportUser.last_name || ''}`.trim(),
        type: 'user' as const,
      })
      existingIds.add(supportUser.id)
    }

    // 3. Add all other users (avoiding duplicates)
    users.forEach((u) => {
      if (!existingIds.has(u.id)) {
        targets.push({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`,
          type: 'user' as const,
        })
        existingIds.add(u.id)
      }
    })

    return targets
  }, [users, supportUser, identity?.role])

  const teamTargets = teams.map((team) => ({
    id: team.id,
    name: team.name,
    type: 'team' as const,
  }))

  return {
    userTargets,
    teamTargets,
    isLoading: usersLoading || teamsLoading || supportUserLoading,
  }
}
